// ========================================
// Color Palette
// ========================================

export const colors = {
  // Primary Colors
  primary: {
    teal: '#1E4C5C',
    tealDark: '#143742',
    orange: '#D97D2B',
    orangeLight: '#F5A64A',
    yellow: '#FCD517',
  },

  // Game-specific colors
  games: {
    bibliquien: '#1D3F84',
    conectados: '#234E9D', // from conectados gradient
  },
  
  // Secondary Colors
  secondary: {
    purple: '#667eea',
    purpleDark: '#5a67d8',
    green: '#43e97b',
    red: '#e1562d',
  },
  
  // Neutral Colors
  neutral: {
    white: '#ffffff',
    gray50: '#f9fafb',
    gray100: '#f5f5f5',
    gray200: '#e0e0e0',
    gray300: '#d1d9e6',
    gray400: '#a0a0a0',
    gray500: '#666666',
    gray600: '#555555',
    gray700: '#333333',
    gray800: '#2d3748',
    gray900: '#1a1a1a',
    black: '#000000',
  },
  
  // Gradients
  gradients: {
    hero: 'linear-gradient(135deg, rgba(30, 76, 92, 0.85) 0%, rgba(217, 125, 43, 0.75) 100%)',
    orange: 'linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%)',
    orangeReverse: 'linear-gradient(135deg, #F5A64A 0%, #D97D2B 100%)',
    teal: 'linear-gradient(135deg, #1E4C5C 0%, #143742 100%)',
    card: 'linear-gradient(135deg, rgba(30, 76, 92, 0.1) 0%, rgba(217, 125, 43, 0.1) 100%)',
    conectados: 'linear-gradient(135deg, rgba(252, 213, 23, 1) 0%, rgba(35, 78, 157, 1) 100%)',
  },
};

// ========================================
// Typography
// ========================================

export const typography = {
  fontFamily: {
    base: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    mono: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
  },
  
  fontSize: {
    xs: '0.75rem',     // 12px
    sm: '0.875rem',    // 14px
    base: '1rem',      // 16px
    lg: '1.125rem',    // 18px
    xl: '1.25rem',     // 20px
    '2xl': '1.5rem',   // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem',  // 36px
    '5xl': '3rem',     // 48px
  },
  
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  
  lineHeight: {
    none: 1,
    tight: 1.1,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.6,
    loose: 2,
  },
};

// ========================================
// Spacing
// ========================================

export const spacing = {
  xs: '0.25rem',    // 4px
  sm: '0.5rem',     // 8px
  md: '1rem',       // 16px
  lg: '1.5rem',     // 24px
  xl: '2rem',       // 32px
  '2xl': '3rem',    // 48px
  '3xl': '4rem',    // 64px
  '4xl': '6rem',    // 96px
};

// ========================================
// Breakpoints
// ========================================

export const breakpoints = {
  xs: '480px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

// ========================================
// Shadows
// ========================================

export const shadows = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px rgba(0, 0, 0, 0.15)',
  card: '0 8px 25px rgba(0, 0, 0, 0.15)',
  cardHover: '0 15px 35px rgba(0, 0, 0, 0.2)',
  orange: '0 8px 25px rgba(217, 125, 43, 0.4)',
  orangeHover: '0 12px 35px rgba(217, 125, 43, 0.6)',
};

// ========================================
// Border Radius
// ========================================

export const borderRadius = {
  none: '0',
  sm: '0.125rem',   // 2px
  base: '0.25rem',  // 4px
  md: '0.375rem',   // 6px
  lg: '0.5rem',     // 8px
  xl: '0.75rem',    // 12px
  '2xl': '1rem',    // 16px
  '3xl': '1.5rem',  // 24px
  full: '9999px',
};

// ========================================
// Transitions
// ========================================

export const transitions = {
  fast: '0.15s ease',
  base: '0.3s ease',
  slow: '0.5s ease',
  
  // Cubic bezier for smooth animations
  smooth: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  bounce: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
};

// ========================================
// Z-Index Layers
// ========================================

export const zIndex = {
  base: 1,
  dropdown: 10,
  sticky: 100,
  fixed: 1000,
  modalBackdrop: 2000,
  modal: 2001,
  popover: 3000,
  tooltip: 4000,
};

// ========================================
// Default Theme Export
// ========================================

const theme = {
  colors,
  typography,
  spacing,
  breakpoints,
  shadows,
  borderRadius,
  transitions,
  zIndex,
};

export default theme;

